import { useCallback, useEffect, useId, useRef, useState } from "react";
import { toApiError } from "../../../lib/http/errors";
import { Button, ButtonLink } from "../../../components/ui/Button";
import { ErrorBanner, Loading } from "../../../components/Shell";
import type { TopBarSpec } from "../../../components/TopBar";
import { useI18n } from "../../../i18n";
import { roomConsolePath } from "../../../constants/paths";
import { whenLabel } from "../../../lib/activityTime";
import { SegGroup } from "../../../components/ui/Seg";
import {
  DEFAULT_EXPIRY_DAYS,
  EXPIRY_DAYS,
  INVITE_STATES,
  InviteConflict,
} from "../api/exchangeApi";
import type { ExchangeApi } from "../api/exchangeApi";
import type { ExchangeCourse, Invite, Invites, InviteAction, Side } from "../types";
import {
  actionsOf,
  busyCourses,
  countPending,
  localToIso,
  partnerOf,
  roomLive,
  sortCourses,
  sortInvites,
} from "../lib/logic";
import "../../../styles/exchange.css";

/** 받은 초대가 새로 오는지 가끔 다시 읽는다(겹쳐 부르지 않는다) */
export const EXCHANGE_POLL_MS = 10_000;

function useExchange(api: ExchangeApi) {
  const [courses, setCourses] = useState<ExchangeCourse[] | null>(null);
  const [invites, setInvites] = useState<Invites | null>(null);
  const [error, setError] = useState<string | null>(null);
  const inflight = useRef(false);
  const load = useCallback(async () => {
    if (inflight.current) return;
    inflight.current = true;
    try {
      const [c, i] = await Promise.all([api.courses(), api.invites()]);
      setCourses(c);
      setInvites(i);
      setError(null);
    } catch (e) {
      setError(toApiError(e).code);
    } finally {
      inflight.current = false;
    }
  }, [api]);
  useEffect(() => {
    void load();
    const h = window.setInterval(() => void load(), EXCHANGE_POLL_MS);
    return () => window.clearInterval(h);
  }, [load]);
  return { courses, invites, error, load };
}

/**
 * FS-11-08 교류 룸 초대·수락(교사 · SCR v0.7 검토 §4 신설 — 와이어 없음, 검토 표가 명세 · 콘솔 탭 틀 · Bench 토큰).
 *  - 같은 기관의 반 목록(코스 label · 최근 Field 사용 코스만 · 교사 이름 없음) → 한 반 고르기 → 예정 시각(지금 바로 / 시각) · 만료(1~30일, 기본 7)
 *    → 초대 보내기(409 같은 쌍 대기 · 403 FE-302 다른 기관)
 *  - 받은 초대: 수락 · 거절 / 보낸 초대: 취소 · 상태 5종(대기 · 수락 · 거절 · 만료 · 취소) 알약
 *  - 수락된 초대 → '교류 룸 열기'(호스트 = 보낸 반) → 콘솔(FS-11-01 공동 호스트 변형). 받은 반은 룸이 열리면 '교류 룸 콘솔 →'
 *  - 룸 코드 입력 없음(SR-AUTH-03) · 반 점수·반 대항 없음(DR-01) · 교사 자유 텍스트 없음(고르기만)
 */
export function ExchangePanel({
  api,
  courseLabel,
  onTop,
  onError,
  onDone,
  onOpened,
}: {
  api: ExchangeApi;
  courseLabel: string;
  onTop: (spec: TopBarSpec) => void;
  onError: (e: unknown) => void;
  onDone: (text: string) => void;
  onOpened: (roomId: string) => void;
}) {
  const { t } = useI18n();
  const { courses, invites, error, load } = useExchange(api);
  const [busy, setBusy] = useState<string | null>(null);
  const pend = invites ? countPending(invites) : null;
  const top = JSON.stringify([courseLabel, pend]);
  useEffect(() => {
    const chips = pend
      ? [
          { label: t("exchange.chipReceived", { n: pend.received }), testId: "xchg-chip-received" },
          { label: t("exchange.chipSent", { n: pend.sent }), testId: "xchg-chip-sent" },
        ]
      : [];
    onTop({ icon: "🤝", title: t("exchange.topTitle", { course: courseLabel }), chips });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [top]);

  const run = async (key: string, fn: () => Promise<void>) => {
    setBusy(key);
    try {
      await fn();
      await load();
    } catch (e) {
      if (e instanceof InviteConflict) onDone(t("exchange.conflict"));
      else onError(e);
    } finally {
      setBusy(null);
    }
  };

  const act = (inv: Invite, side: Side, a: InviteAction) => {
    const label = partnerOf(inv, side).label;
    if (a === "open")
      return run(`open:${inv.inviteId}`, async () => {
        const roomId = await api.open(inv.inviteId);
        onOpened(roomId);
      });
    if (a === "console") return;
    return run(`${a}:${inv.inviteId}`, async () => {
      await api.respond(inv.inviteId, a);
      onDone(t(`exchange.done.${a}`, { label }));
    });
  };

  return (
    <div className="pf-xchg" data-testid="exchange-panel">
      {error && (
        <ErrorBanner
          cause={t("exchange.loadFailed")}
          action={t("teacher.common.retryLater")}
          code={error}
        />
      )}
      {!courses || !invites ? (
        !error && <Loading />
      ) : (
        <>
          <SendPane
            courses={courses}
            invites={invites}
            busy={busy}
            onSend={(body, label) =>
              void run("send", async () => {
                await api.send(body);
                onDone(t("exchange.done.sent", { label }));
              })
            }
          />
          <div className="pf-xchg__lists">
            <InviteList
              side="received"
              items={invites.received}
              busy={busy}
              onAct={(inv, a) => void act(inv, "received", a)}
            />
            <InviteList
              side="sent"
              items={invites.sent}
              busy={busy}
              onAct={(inv, a) => void act(inv, "sent", a)}
            />
          </div>
          <div className="pf-banner pf-banner--info" role="note" data-testid="xchg-scope-note">
            <div className="pf-banner__text">{t("exchange.scopeNote")}</div>
          </div>
        </>
      )}
    </div>
  );
}

type When = "now" | "at";

/** 초대 보내기 — 반 고르기 · 예정 시각 · 만료 */
function SendPane({
  courses,
  invites,
  busy,
  onSend,
}: {
  courses: ExchangeCourse[];
  invites: Invites;
  busy: string | null;
  onSend: (
    body: { toCourseId: string; scheduledAt?: string; expiresInDays: number },
    label: string,
  ) => void;
}) {
  const { t } = useI18n();
  const id = useId();
  const [pick, setPick] = useState<string | null>(null);
  const [when, setWhen] = useState<When>("now");
  const [at, setAt] = useState("");
  const [days, setDays] = useState(DEFAULT_EXPIRY_DAYS);
  const taken = busyCourses(invites);
  const sorted = sortCourses(courses);
  const chosen = sorted.find((c) => c.courseId === pick && !taken.has(c.courseId)) ?? null;
  const iso = when === "at" ? localToIso(at) : null;
  const ready = !!chosen && (when === "now" || iso !== null) && busy === null;

  return (
    <section
      className="pf-pane pf-pane--hero pf-xchg__send"
      aria-labelledby={`${id}-t`}
      data-testid="xchg-send"
    >
      <h2 id={`${id}-t`} className="pf-h3">
        {t("exchange.sendTitle")}
      </h2>
      <p className="pf-muted">{t("exchange.sendHint")}</p>
      {sorted.length === 0 ? (
        <p className="pf-muted" data-testid="xchg-no-courses">
          {t("exchange.noCourses")}
        </p>
      ) : (
        <div
          className="pf-xchg__courses"
          role="radiogroup"
          aria-label={t("exchange.courseList")}
          data-testid="xchg-courses"
        >
          {sorted.map((c) => {
            const st = taken.get(c.courseId);
            return (
              <label
                key={c.courseId}
                className={`pf-xchg__course${st ? " pf-xchg__course--busy" : ""}`}
                data-course={c.courseId}
              >
                <input
                  type="radio"
                  name={`${id}-course`}
                  value={c.courseId}
                  checked={pick === c.courseId && !st}
                  disabled={!!st}
                  onChange={() => setPick(c.courseId)}
                />
                <span className="pf-xchg__course-body">
                  <b className="pf-xchg__course-label">{c.label}</b>
                  <span className="pf-muted">
                    {t("exchange.teacherCount", { n: c.teacherCount })}
                  </span>
                  {st && (
                    <span
                      className={`pf-chip-static ${st === "pending" ? "pf-chip-static--info" : "pf-chip-static--ok"}`}
                    >
                      {t(`exchange.busy.${st}`)}
                    </span>
                  )}
                </span>
              </label>
            );
          })}
        </div>
      )}
      <div className="pf-xchg__opts">
        <SegGroup label={t("exchange.whenLabel")} testId="xchg-when">
          {(["now", "at"] as const).map((w) => (
            <label key={w} className="pf-seg__opt">
              <input
                type="radio"
                name={`${id}-when`}
                value={w}
                checked={when === w}
                onChange={() => setWhen(w)}
              />
              <span>{t(`exchange.when.${w}`)}</span>
            </label>
          ))}
          {when === "at" && (
            <input
              type="datetime-local"
              className="pf-xchg__at"
              aria-label={t("exchange.whenAt")}
              value={at}
              onChange={(e) => setAt(e.target.value)}
              data-testid="xchg-at"
            />
          )}
        </SegGroup>
        <SegGroup label={t("exchange.expiryLabel")} testId="xchg-expiry">
          {EXPIRY_DAYS.map((d) => (
            <label key={d} className="pf-seg__opt">
              <input
                type="radio"
                name={`${id}-exp`}
                value={d}
                checked={days === d}
                onChange={() => setDays(d)}
              />
              <span>{t("exchange.days", { n: d })}</span>
            </label>
          ))}
        </SegGroup>
      </div>
      <div className="pf-row">
        <Button
          variant="accent"
          icon="📨"
          disabled={!ready}
          data-testid="xchg-send-btn"
          onClick={() =>
            chosen &&
            onSend(
              {
                toCourseId: chosen.courseId,
                ...(iso ? { scheduledAt: iso } : {}),
                expiresInDays: days,
              },
              chosen.label,
            )
          }
        >
          {chosen ? t("exchange.sendTo", { label: chosen.label }) : t("exchange.send")}
        </Button>
        <span className="pf-muted">{t("exchange.noCode")}</span>
      </div>
    </section>
  );
}

function InviteList({
  side,
  items,
  busy,
  onAct,
}: {
  side: Side;
  items: Invite[];
  busy: string | null;
  onAct: (inv: Invite, a: InviteAction) => void;
}) {
  const { t } = useI18n();
  const id = useId();
  return (
    <section className="pf-pane" aria-labelledby={id} data-testid={`xchg-${side}`}>
      <h2 id={id} className="pf-h3">
        {t(`exchange.${side}Title`)}
      </h2>
      {items.length === 0 ? (
        <p className="pf-muted" data-testid={`xchg-${side}-empty`}>
          {t(`exchange.${side}Empty`)}
        </p>
      ) : (
        <ul className="pf-xchg__list">
          {sortInvites(items).map((inv) => (
            <InviteItem
              key={inv.inviteId}
              inv={inv}
              side={side}
              busy={busy}
              onAct={(a) => onAct(inv, a)}
            />
          ))}
        </ul>
      )}
    </section>
  );
}

function InviteItem({
  inv,
  side,
  busy,
  onAct,
}: {
  inv: Invite;
  side: Side;
  busy: string | null;
  onAct: (a: InviteAction) => void;
}) {
  const { t, locale } = useI18n();
  const partner = partnerOf(inv, side);
  const actions = actionsOf(inv, side);
  const when = inv.scheduledAt ? whenLabel(inv, t, locale) : null;
  const day = (iso: string | null) => {
    const ms = Date.parse(iso ?? "");
    return Number.isFinite(ms)
      ? new Date(ms).toLocaleDateString(locale, { month: "long", day: "numeric" })
      : null;
  };
  const expires = day(inv.expiresAt);
  const responded = day(inv.respondedAt);
  const live = roomLive(inv);
  const meta = [
    when ? t("exchange.meta.when", { when }) : t("exchange.meta.whenOpen"),
    inv.state === "pending" && expires ? t("exchange.meta.expires", { date: expires }) : null,
    inv.state !== "pending" && responded
      ? t(`exchange.meta.responded.${inv.state}`, { date: responded })
      : null,
  ].filter(Boolean);

  return (
    <li
      className={`pf-xchg__item pf-xchg__item--${inv.state}`}
      data-invite={inv.inviteId}
      data-state={inv.state}
      data-testid="xchg-item"
    >
      <div className="pf-xchg__head">
        <b className="pf-xchg__who">
          {t(side === "sent" ? "exchange.toLabel" : "exchange.fromLabel", {
            label: partner.label,
          })}
        </b>
        {live && (
          <span className="pf-chip-static pf-chip-static--run" data-testid="xchg-room-live">
            {t("exchange.roomLive")}
          </span>
        )}
      </div>
      <ol className="pf-state" aria-label={t("exchange.stateLabel")}>
        {INVITE_STATES.map((s) => (
          <li
            key={s}
            className={`pf-state__pill${inv.state === s ? " pf-state__pill--on" : ""}`}
            aria-current={inv.state === s ? "step" : undefined}
            data-state={s}
          >
            {t(`exchange.state.${s}`)}
          </li>
        ))}
      </ol>
      <p className="pf-muted pf-xchg__meta">{meta.join(" · ")}</p>
      {inv.state === "accepted" && !live && side === "received" && (
        <p className="pf-muted" data-testid="xchg-wait-host">
          {t("exchange.waitHost", { label: partner.label })}
        </p>
      )}
      {actions.length > 0 && (
        <div className="pf-row pf-xchg__actions">
          {actions.map((a) =>
            a === "console" ? (
              <ButtonLink
                key={a}
                to={roomConsolePath(inv.roomId!)}
                variant="green"
                className="pf-btn--sm"
                data-act="console"
              >
                {t("exchange.act.console")}
              </ButtonLink>
            ) : (
              <Button
                key={a}
                variant={a === "accept" || a === "open" ? "accent" : "ghost"}
                className="pf-btn--sm"
                disabled={busy !== null}
                aria-label={t(`exchange.actOf.${a}`, { label: partner.label })}
                data-act={a}
                onClick={() => onAct(a)}
              >
                {t(`exchange.act.${a}`)}
              </Button>
            ),
          )}
        </div>
      )}
    </li>
  );
}
